import { Component } from '@theme/component';

/**
 * DAVIDsTEA About us recipes rail.
 *
 * Same shape as dt-matcha-recipes, and deliberately so: two recipe rails on two pages
 * should not answer a pointer differently. The rail is a native overflow container with
 * snap points, so a finger, a trackpad and the keyboard all work with no script. What
 * this adds is the previous/next pair a desktop mouse has no other way to reach.
 *
 * The arrows stay hidden until this runs, and they only come in when the rail overflows what
 * it can show: `data-overflow`, re-read on every scroll and every resize, so the criterion
 * follows the window and not the number of cards. A pair that cannot move anything is worse
 * than none, and with the cards the band carries the row fits whole at 2560, where the pair
 * would otherwise sit dimmed at both ends. Whenever it is on screen both arrows stay in place
 * and the one at its end of the rail dims rather than disappearing, so the other never slides
 * under the cursor. Same rule everywhere in the theme.
 *
 * The step is measured rather than hard-coded: one card plus one gap, read from the
 * first two items. A card size change in the stylesheet moves the arrows with it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement} prev
 * @property {HTMLButtonElement} next
 *
 * @extends {Component<Refs>}
 */
export class DtAboutRecipes extends Component {
  requiredRefs = ['rail', 'prev', 'next'];

  connectedCallback() {
    super.connectedCallback();

    const { rail, prev, next } = this.refs;

    prev.addEventListener('click', this.#onPrev);
    next.addEventListener('click', this.#onNext);
    rail.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#observer = new ResizeObserver(this.#syncEnds);
    this.#observer.observe(rail);

    this.#syncEnds();
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    const { rail, prev, next } = this.refs;
    prev?.removeEventListener('click', this.#onPrev);
    next?.removeEventListener('click', this.#onNext);
    rail?.removeEventListener('scroll', this.#onScroll);
    this.#observer?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #observer;

  /**
   * One card plus one gap. Falls back to the card width alone when a single card is
   * left, and to zero when the rail is empty, which the callers already guard.
   */
  get #step() {
    const items = this.refs.rail.querySelectorAll('.dt-about-recipes__card');
    const first = items[0];
    if (!(first instanceof HTMLElement)) return 0;
    const second = items[1];
    if (second instanceof HTMLElement) {
      return second.getBoundingClientRect().left - first.getBoundingClientRect().left;
    }
    return first.getBoundingClientRect().width;
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /** @param {number} direction */
  #move(direction) {
    const step = this.#step;
    if (!step) return;
    this.refs.rail.scrollBy({ left: step * direction, behavior: this.#behavior });
  }

  #onPrev = () => this.#move(-1);
  #onNext = () => this.#move(1);
  #onScroll = () => this.#syncEnds();

  /**
   * A single pixel of slack on each end: browsers land on fractional scroll offsets,
   * and an exact comparison leaves the arrow live at a stop it cannot move past.
   */
  #syncEnds = () => {
    const { rail, prev, next } = this.refs;
    const max = rail.scrollWidth - rail.clientWidth;
    this.toggleAttribute('data-overflow', max > 1);
    prev.disabled = rail.scrollLeft <= 1;
    next.disabled = max <= 1 || rail.scrollLeft >= max - 1;
  };
}

if (!customElements.get('dt-about-recipes-component')) {
  customElements.define('dt-about-recipes-component', DtAboutRecipes);
}
